import { useQuery, useMutation, useQueryClient, QueryObserverOptions } from "@tanstack/react-query";
import { apiGet, apiPut } from "../client";
import { useLoggedInUser } from "./useAuth";

interface CommonQueryOptions {
	keepPreviousData?: QueryObserverOptions["keepPreviousData"];
	onSuccess?: QueryObserverOptions["onSuccess"];
	refetchOnWindowFocus?: boolean;
}

/**
 * Hook to update a customer
 */
export function useCustomerUpdate(onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ customerId, body }: { customerId: string; body: any }) => apiPut(`/customer/${customerId}`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["update-customer"] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Fetches all customers that the logged in customer user has access to
 */
export function useCustomerUsersCustomers(onError?: (errorCode: string) => void, opts: CommonQueryOptions = {}) {
	const user = useLoggedInUser();

	const { data, ...rest } = useQuery<any>({
		queryKey: ["customer-account", user?.id, "customer"],
		queryFn: () => apiGet<any>(`/customer-account/${user.id}/customer`),
		refetchOnWindowFocus: false,
		staleTime: 5 * 1000,
		...opts,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		customers: data,
		...rest,
	};
}

/**
 * Hook to get customer account data for a user
 */
export function useCustomerAccountData(userId?: string, onError?: (errorCode: string) => void) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["customerAccount", userId],
		queryFn: () => apiGet<any>(`/customer-account/${userId}`),
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		customerAccountUser: data,
		...rest,
	};
}

/**
 * Hook to update customer account
 */
export function useCustomerAccount(onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, body }: { userId: string; body: any }) => apiPut(`/customer-account/${userId}`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["update-customer-account"] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to update customer account notification settings
 */
export function useCustomerAccountNotificationSettings(onSuccess?: () => any, onError?: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, customerId, body }: { userId: string; customerId: string; body: any }) =>
			apiPut(`/customer-account/${userId}/${customerId}/notification-settings`, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["update-customer-account-notification-settings"] });
			onSuccess && onSuccess();
		},
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to get customer competences
 */
export function useCustomerCompetences(
	customerId?: string,
	onSuccess?: () => any,
	onError?: (errorCode: string) => void,
) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["customer", customerId, "competences"],
		queryFn: () => apiGet<any>(`/customer/${customerId}/competences`),
		enabled: !!customerId,
		staleTime: 30 * 60 * 1000,
		gcTime: 60 * 60 * 1000,
		onSuccess,
		refetchOnWindowFocus: false,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		competences: data?.competences,
		...rest,
	};
}

/**
 * Hook to get customer terms
 */
export function useCustomerTerms(customerId?: string, onSuccess?: () => any, onError?: (errorCode: string) => void) {
	return useQuery<any>({
		queryKey: ["customer", customerId, "terms"],
		queryFn: () => apiGet<any>(`/customer/${customerId}/terms`),
		enabled: !!customerId,
		staleTime: 30 * 60 * 1000,
		gcTime: 60 * 60 * 1000,
		refetchOnWindowFocus: false,
		onSuccess,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});
}

/**
 * Hook to get customer users
 */
export function useCustomerUsers(customerId?: string, onError?: (errorCode: string) => void) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["customer", customerId, "user"],
		queryFn: () => apiGet<any>(`/customer/${customerId}/user`),
		enabled: !!customerId,
		refetchOnWindowFocus: false,
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		customerUsers: data,
		...rest,
	};
}

/**
 * Hook to get a single customer by ID
 */
export function useCustomer(customerId: string, onError?: (errorCode: string) => void) {
	const { data, ...rest } = useQuery<any>({
		queryKey: ["customer", customerId],
		queryFn: () => apiGet<any>(`/customer/${customerId}`),
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return {
		customer: data,
		...rest,
	};
}
